import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Collapse, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useState } from 'react';
import {
  FILTER_BY_LABELS,
  FilterByOption,
} from '~/modules/matters/useMattersList';

export interface AttorneyOption {
  id: string;
  name: string;
}

interface Props {
  attorneyFilterId: string | null;
  filterBy: FilterByOption;
  attorneyOptions: AttorneyOption[];
  onAttorneyChange: (attorneyId: string | null) => void;
  onFilterByChange: (value: FilterByOption) => void;
}

const ExpertMattersFilter = ({
  attorneyFilterId,
  filterBy,
  attorneyOptions,
  onAttorneyChange,
  onFilterByChange,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [attorneysOpen, setAttorneysOpen] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setAttorneysOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setAnchorEl(event.currentTarget as unknown as HTMLElement);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAttorneysOpen(false);
  };

  const handleAttorneysClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAttorneysOpen((open) => !open);
  };

  const handleAttorneySelect = (id: string) => {
    onAttorneyChange(id);
    setAnchorEl(null);
    setAttorneysOpen(false);
  };

  const handleShowAllAttorneys = () => {
    onAttorneyChange(null);
    setAnchorEl(null);
    setAttorneysOpen(false);
  };

  const handleFilterBySelect = (value: FilterByOption) => {
    onFilterByChange(value);
    setAnchorEl(null);
    setAttorneysOpen(false);
  };

  const selectedAttorneyName =
    attorneyFilterId !== null
      ? attorneyOptions.find((option) => option.id === attorneyFilterId)?.name
      : null;
  const valueLabel = selectedAttorneyName ?? FILTER_BY_LABELS[filterBy];

  return (
    <>
      <Trigger
        aria-label="Filter matters"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleOpen}
      >
        <FilterList fontSize="small" color="action" aria-hidden />
        <FilterLabel>Filter: {valueLabel}</FilterLabel>
        <KeyboardArrowDown fontSize="small" color="action" aria-hidden />
      </Trigger>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          key={FilterByOption.All}
          onClick={() => {
            onAttorneyChange(null);
            onFilterByChange(FilterByOption.All);
            setAnchorEl(null);
            setAttorneysOpen(false);
          }}
        >
          <ListItemText primary={FILTER_BY_LABELS[FilterByOption.All]} />
        </MenuItem>
        <MenuItem onClick={handleAttorneysClick} selected={attorneysOpen}>
          <ListItemText primary="Attorneys" />
          <StyledKeyboardArrowRight
            fontSize="small"
            color="action"
            $expanded={attorneysOpen}
            aria-hidden
          />
        </MenuItem>
        <Collapse in={attorneysOpen} timeout="auto" unmountOnExit>
          <StyledMenuItem onClick={handleShowAllAttorneys}>
            <ListItemText primary="Show all" />
          </StyledMenuItem>
          {attorneyOptions.map(({ id, name }) => (
            <StyledMenuItem key={id} onClick={() => handleAttorneySelect(id)}>
              <ListItemText primary={name} />
            </StyledMenuItem>
          ))}
        </Collapse>
        {(Object.keys(FILTER_BY_LABELS) as FilterByOption[])
          .filter((key) => key !== FilterByOption.All)
          .map((key) => (
            <MenuItem key={key} onClick={() => handleFilterBySelect(key)}>
              <ListItemText primary={FILTER_BY_LABELS[key]} />
            </MenuItem>
          ))}
      </StyledMenu>
    </>
  );
};

const FilterLabel = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 500,
}));

const Trigger = styled(Stack)(({ theme }: { theme: Theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.mattersDropdown.trigger,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.mattersDropdown.triggerHover,
  },
}));

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': { minWidth: 200 },
});

const StyledKeyboardArrowRight = styled(KeyboardArrowRight)<{
  $expanded?: boolean;
}>(({ theme, $expanded }) => ({
  transform: $expanded ? 'rotate(90deg)' : 'none',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shorter,
  }),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

export default ExpertMattersFilter;
